import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { timePunchAPI, timeOffAPI, leaveBalanceAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import type { TimePunch, ClockStatus, LeaveBalance, TimeOffEntry } from '../types';

const TimeClock: React.FC = () => {
  const { user } = useAuth();
  const [clockStatus, setClockStatus] = useState<ClockStatus | null>(null);
  const [punches, setPunches] = useState<TimePunch[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [flagPunchId, setFlagPunchId] = useState<number | null>(null);
  const [flagNote, setFlagNote] = useState('');
  const [flagError, setFlagError] = useState('');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [leaveBalances, setLeaveBalances] = useState<LeaveBalance[]>([]);
  const [timeOffEntries, setTimeOffEntries] = useState<TimeOffEntry[]>([]);

  const fetchStatus = useCallback(async () => {
    try {
      const response = await timePunchAPI.currentStatus();
      setClockStatus(response.data);
    } catch (err) {
      console.error('Failed to fetch clock status:', err);
    }
  }, []);

  const fetchPunches = useCallback(async () => {
    try {
      const response = await timePunchAPI.getAll({ start_date: getStartOfWeek() });
      setPunches(response.data.results);
    } catch (err) {
      console.error('Failed to fetch punches:', err);
    }
  }, []);

  const fetchTimeOff = useCallback(async () => {
    try {
      const resp = await timeOffAPI.getAll({ start_date: getStartOfWeek() });
      setTimeOffEntries(resp.data.results || []);
    } catch {
      setTimeOffEntries([]);
    }
  }, []);

  const fetchLeaveBalances = useCallback(async () => {
    try {
      const currentYear = new Date().getFullYear();
      const resp = await leaveBalanceAPI.getAll({ year: currentYear });
      setLeaveBalances(resp.data.results || []);
    } catch {
      setLeaveBalances([]);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchStatus(), fetchPunches(), fetchTimeOff(), fetchLeaveBalances()]);
      setLoading(false);
    };
    loadData();
  }, [fetchStatus, fetchPunches, fetchTimeOff, fetchLeaveBalances]);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const handleClockIn = async () => {
    setActionLoading(true);
    setError('');
    setSuccess('');
    try {
      await timePunchAPI.clockIn();
      setSuccess('Clocked in successfully!');
      await Promise.all([fetchStatus(), fetchPunches()]);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to clock in.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleClockOut = async () => {
    setActionLoading(true);
    setError('');
    setSuccess('');
    try {
      await timePunchAPI.clockOut();
      setSuccess('Clocked out successfully!');
      await Promise.all([fetchStatus(), fetchPunches()]);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to clock out.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleFlag = async (punchId: number) => {
    if (!flagNote.trim()) {
      setFlagError('Please explain what needs correction.');
      return;
    }
    setFlagError('');
    try {
      await timePunchAPI.flag(punchId, flagNote.trim());
      setFlagPunchId(null);
      setFlagNote('');
      setSuccess('Entry flagged for review.');
      await fetchPunches();
    } catch (err: any) {
      setFlagError(err.response?.data?.error || 'Failed to flag entry.');
    }
  };

  if (loading) {
    return <div className="container"><div className="loading">Loading...</div></div>;
  }

  const isClockedIn = clockStatus?.clocked_in ?? false;
  const activePunch = clockStatus?.punch;

  return (
    <div className="container">
      <div className="card">
        <h2>Time Clock</h2>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <p style={{ color: '#7f8c8d', margin: 0 }}>
            Welcome, {user?.first_name || user?.username}
          </p>
          <Link to="/timesheet-history" className="btn btn-small btn-secondary">
            View Timesheet History
          </Link>
        </div>

        {error && <div className="error">{error}</div>}
        {success && <div className="success">{success}</div>}

        <div style={{
          textAlign: 'center',
          padding: '2rem',
          backgroundColor: isClockedIn ? '#e8f8f5' : '#f8f9fa',
          borderRadius: '8px',
          border: `2px solid ${isClockedIn ? '#27ae60' : '#ddd'}`,
          marginBottom: '2rem',
        }}>
          <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#2c3e50', marginBottom: '0.5rem' }}>
            {currentTime.toLocaleTimeString()}
          </div>
          <div style={{ fontSize: '1.1rem', color: '#7f8c8d', marginBottom: '1.5rem' }}>
            {currentTime.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <div style={{
              fontSize: '1.15rem',
              fontWeight: '600',
              color: isClockedIn ? '#27ae60' : '#7f8c8d',
              marginBottom: '0.25rem',
            }}>
              {isClockedIn ? 'You are currently on the clock' : 'You are not clocked in'}
            </div>
            {isClockedIn && activePunch && (
              <div style={{ color: '#7f8c8d', fontSize: '0.95rem' }}>
                Since {new Date(activePunch.clock_in).toLocaleTimeString()}
                {' '}({getElapsedTime(activePunch.clock_in)})
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
            <button
              className="btn btn-success"
              style={{
                fontSize: '1.2rem',
                padding: '1rem 2.5rem',
                minWidth: '160px',
                opacity: isClockedIn ? 0.35 : 1,
                cursor: isClockedIn ? 'not-allowed' : 'pointer',
              }}
              onClick={handleClockIn}
              disabled={isClockedIn || actionLoading}
            >
              {actionLoading && !isClockedIn ? 'Processing...' : 'Clock In'}
            </button>
            <button
              className="btn btn-danger"
              style={{
                fontSize: '1.2rem',
                padding: '1rem 2.5rem',
                minWidth: '160px',
                opacity: !isClockedIn ? 0.35 : 1,
                cursor: !isClockedIn ? 'not-allowed' : 'pointer',
              }}
              onClick={handleClockOut}
              disabled={!isClockedIn || actionLoading}
            >
              {actionLoading && isClockedIn ? 'Processing...' : 'Clock Out'}
            </button>
          </div>
        </div>
      </div>

      {leaveBalances.filter(b => !(b.leave_type === 'vacation' && Number(b.granted_hours) === 0 && Number(b.carryover_hours) === 0)).length > 0 && (
        <div className="card">
          <h3 style={{ marginBottom: '1rem', color: '#2c3e50' }}>Leave Balances</h3>
          <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
            {leaveBalances.filter(b => !(b.leave_type === 'vacation' && Number(b.granted_hours) === 0 && Number(b.carryover_hours) === 0)).map(b => (
              <div key={b.id} style={{
                flex: '1 1 200px',
                padding: '1rem 1.5rem',
                borderRadius: '8px',
                backgroundColor: b.leave_type === 'sick' ? '#fef0f0' : '#eff6ff',
                border: `1px solid ${b.leave_type === 'sick' ? '#f5c6cb' : '#bee3f8'}`,
              }}>
                <div style={{ fontWeight: 'bold', fontSize: '1.1rem', marginBottom: '0.25rem', color: '#2c3e50' }}>
                  {b.leave_type === 'sick' ? 'Sick Leave' : 'Vacation'}
                </div>
                <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: b.leave_type === 'sick' ? '#e74c3c' : '#3498db' }}>
                  {b.available_hours}h
                </div>
                <div style={{ color: '#7f8c8d', fontSize: '0.85rem' }}>
                  available ({b.used_hours}h used of {(Number(b.carryover_hours) + Number(b.granted_hours)).toFixed(0)}h)
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card">
        <h3 style={{ marginBottom: '1rem', color: '#2c3e50' }}>Recent Time</h3>

        {punches.length === 0 && timeOffEntries.length === 0 ? (
          <p style={{ color: '#95a5a6' }}>No recent time records found.</p>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Details</th>
                  <th>Hours</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ...punches.map(p => ({ kind: 'punch' as const, sortDate: p.clock_in, punch: p })),
                  ...timeOffEntries.map(e => ({ kind: 'timeoff' as const, sortDate: e.date + 'T00:00:00', timeOff: e })),
                ].sort((a, b) => new Date(b.sortDate).getTime() - new Date(a.sortDate).getTime())
                .map(row => {
                  if (row.kind === 'punch') {
                    const punch = row.punch!;
                    return (
                      <React.Fragment key={`p-${punch.id}`}>
                        <tr style={punch.is_flagged ? { backgroundColor: '#fff8e1' } : undefined}>
                          <td>{formatDate(punch.clock_in)}</td>
                          <td>Punch</td>
                          <td>{formatTime(punch.clock_in)} – {punch.clock_out ? formatTime(punch.clock_out) : <em style={{ color: '#27ae60' }}>Active</em>}</td>
                          <td>{punch.calculated_hours ?? '—'}</td>
                          <td>{renderPunchStatus(punch)}</td>
                          <td className="actions">
                            {!punch.is_flagged && punch.clock_out && (
                              <button
                                className="btn btn-small btn-secondary"
                                onClick={() => {
                                  setFlagPunchId(flagPunchId === punch.id ? null : punch.id!);
                                  setFlagNote('');
                                  setFlagError('');
                                }}
                              >
                                Flag for Review
                              </button>
                            )}
                          </td>
                        </tr>
                        {flagPunchId === punch.id && (
                          <tr>
                            <td colSpan={6} style={{ backgroundColor: '#fafafa', padding: '1rem' }}>
                              <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
                                <textarea
                                  placeholder="Explain what needs to be corrected..."
                                  value={flagNote}
                                  onChange={(e) => setFlagNote(e.target.value)}
                                  style={{
                                    flex: 1,
                                    padding: '0.5rem',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                    minHeight: '60px',
                                    fontSize: '0.9rem',
                                  }}
                                />
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                  <button className="btn btn-small btn-primary" onClick={() => handleFlag(punch.id!)}>
                                    Submit
                                  </button>
                                  <button
                                    className="btn btn-small btn-secondary"
                                    onClick={() => { setFlagPunchId(null); setFlagNote(''); setFlagError(''); }}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                              {flagError && <div className="error" style={{ marginTop: '0.5rem' }}>{flagError}</div>}
                            </td>
                          </tr>
                        )}
                        {punch.is_flagged && (
                          <tr>
                            <td colSpan={6} style={{
                              backgroundColor: punch.flag_resolved ? '#e8f8f5' : '#fff8e1',
                              padding: '0.5rem 0.75rem',
                              fontSize: '0.85rem',
                            }}>
                              <strong>Your note:</strong> {punch.flag_note}
                              {punch.flag_resolved && punch.flag_resolution_note && (
                                <div style={{ marginTop: '0.25rem', color: '#27ae60' }}>
                                  <strong>Resolution:</strong> {punch.flag_resolution_note}
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  }
                  const entry = row.timeOff!;
                  return (
                    <tr key={`to-${entry.id}`} style={{ backgroundColor: '#f0f8ff' }}>
                      <td>{new Date(entry.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</td>
                      <td>
                        <span style={{
                          display: 'inline-block', padding: '0.1rem 0.5rem', borderRadius: '10px',
                          fontSize: '0.8rem', color: 'white',
                          backgroundColor: entry.time_off_type === 'sick' ? '#e74c3c' : entry.time_off_type === 'vacation' ? '#3498db' : '#27ae60',
                        }}>
                          {entry.time_off_type === 'sick' ? 'Sick' : entry.time_off_type === 'vacation' ? 'Vacation' : 'Holiday'}
                        </span>
                      </td>
                      <td style={{ color: '#7f8c8d' }}>{entry.notes || '—'}</td>
                      <td>{entry.hours}</td>
                      <td><span className="status-badge" style={{ backgroundColor: '#8e44ad', color: 'white' }}>Time Off</span></td>
                      <td></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

function getStartOfWeek(): string {
  const now = new Date();
  const dayOfWeek = now.getDay();
  const diff = now.getDate() - dayOfWeek;
  const startOfWeek = new Date(now.setDate(diff - 7));
  return startOfWeek.toISOString().split('T')[0];
}

function getElapsedTime(clockInStr: string): string {
  const diff = Date.now() - new Date(clockInStr).getTime();
  const hours = Math.floor(diff / 3600000);
  const minutes = Math.floor((diff % 3600000) / 60000);
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric',
  });
}

function formatTime(dateStr: string): string {
  return new Date(dateStr).toLocaleTimeString('en-US', {
    hour: 'numeric', minute: '2-digit',
  });
}

function renderPunchStatus(punch: TimePunch): React.ReactNode {
  if (punch.is_open) {
    return <span className="status-badge status-active">Active</span>;
  }
  if (punch.is_flagged && !punch.flag_resolved) {
    return <span className="status-badge" style={{ backgroundColor: '#f39c12', color: 'white' }}>Flagged</span>;
  }
  if (punch.is_flagged && punch.flag_resolved) {
    return <span className="status-badge status-paid">Resolved</span>;
  }
  return <span className="status-badge status-draft">Complete</span>;
}

export default TimeClock;
